// 비밀번호 정책 검증 유틸.
// UI 실시간 피드백(조건 칩)과 제출 검증에서 공통으로 사용한다.
// 최종 판정은 서버가 담당하며, 여기서는 동일 규칙을 클라이언트에서 먼저 안내한다.

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 64;

export interface PasswordRuleState {
  /** 8자 ~ 64자 */
  length: boolean;
  /** 영문 포함 */
  letter: boolean;
  /** 숫자 포함 */
  number: boolean;
  /** 특수문자 포함 */
  special: boolean;
}

/** 조건 칩에 쓰는 개별 규칙 충족 여부 */
export function checkPasswordRules(password: string): PasswordRuleState {
  return {
    length:
      password.length >= PASSWORD_MIN_LENGTH &&
      password.length <= PASSWORD_MAX_LENGTH,
    letter: /[a-zA-Z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[^a-zA-Z0-9]/.test(password),
  };
}

/** 길이 + 영문/숫자/특수문자 조합을 모두 만족하는지 */
export function isPasswordFormatValid(password: string): boolean {
  const rules = checkPasswordRules(password);
  return rules.length && rules.letter && rules.number && rules.special;
}

/** 아이디와 동일하거나 아이디를 포함한 비밀번호인지 (이메일이면 로컬파트도 비교) */
export function isPasswordSimilarToLoginId(
  password: string,
  loginId?: string | null,
): boolean {
  if (!loginId) return false;
  const pw = password.toLowerCase();
  const id = loginId.toLowerCase();
  const localPart = id.split("@")[0];
  return (
    (id.length > 0 && pw.includes(id)) ||
    (localPart.length >= 3 && pw.includes(localPart))
  );
}

const isAlphaNumeric = (ch: string) => /[a-zA-Z0-9]/.test(ch);

/**
 * 동일하거나 연속된 숫자·알파벳이 3자 이상 포함됐는지.
 * 예) aaa, abc, cba, 111, 123, 321
 */
export function hasSequentialChars(password: string): boolean {
  for (let i = 0; i + 2 < password.length; i++) {
    const [c0, c1, c2] = [password[i], password[i + 1], password[i + 2]];
    if (!isAlphaNumeric(c0) || !isAlphaNumeric(c1) || !isAlphaNumeric(c2)) {
      continue;
    }
    const [a, b, c] = [c0.charCodeAt(0), c1.charCodeAt(0), c2.charCodeAt(0)];
    // 동일 문자 3연속 (aaa, 111)
    if (a === b && b === c) return true;
    // 연속 증가 (abc, 123)
    if (b - a === 1 && c - b === 1) return true;
    // 연속 감소 (cba, 321)
    if (a - b === 1 && b - c === 1) return true;
  }
  return false;
}
